import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { socialApi, SocialAccount, SocialPlatform } from '@/services/socialApi';

interface SocialPublishModalProps {
  sourceType?: 'clip' | 'upload' | 'text';
  sourceId?: string;
  defaultCaption?: string;
  defaultHashtags?: string[];
  onClose: () => void;
  onPublished: () => void;
}

const platformLabel: Record<SocialPlatform, string> = {
  facebook: 'Facebook Page',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp Business',
};

export default function SocialPublishModal({ sourceType = 'clip', sourceId, defaultCaption = '', defaultHashtags = [], onClose, onPublished }: SocialPublishModalProps) {
  const { data, isLoading } = useQuery({ queryKey: ['social-accounts'], queryFn: () => socialApi.getAccounts() });
  const accounts = data?.accounts || [];
  const [accountId, setAccountId] = useState('');
  const [caption, setCaption] = useState(defaultCaption);
  const [hashtags, setHashtags] = useState(defaultHashtags.join(' '));
  const [recipient, setRecipient] = useState('');
  const [scheduledFor, setScheduledFor] = useState('');
  const [useOrgBranding, setUseOrgBranding] = useState(true);
  const [applyFadeOut, setApplyFadeOut] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const selectedAccount = useMemo(() => accounts.find((account) => account._id === accountId), [accounts, accountId]);
  const isWhatsApp = selectedAccount?.platform === 'whatsapp';
  const isTextOnly = sourceType === 'text';

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedAccount) return toast.error('Select a connected channel');
    if (isWhatsApp && !recipient.trim()) return toast.error('Enter a WhatsApp recipient number with country code');
    setPublishing(true);
    try {
      const response = await socialApi.publish({
        platform: selectedAccount.platform,
        accountId: selectedAccount._id,
        sourceType,
        sourceId,
        contentType: isWhatsApp ? 'message' : isTextOnly ? 'post' : 'video',
        caption,
        hashtags: hashtags.split(/[\s,]+/).map((tag) => tag.replace(/^#/, '')).filter(Boolean),
        recipient: isWhatsApp ? recipient.trim() : undefined,
        scheduledFor: scheduledFor || undefined,
        idempotencyKey: crypto.randomUUID(),
        ...(!isWhatsApp && !isTextOnly ? { useOrgBranding, applyFadeOut } : {}),
      });
      toast.success(response.message);
      onPublished();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to queue social publish job');
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <form onSubmit={submit} className="w-full max-w-xl rounded-xl bg-white shadow-2xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div><h2 className="text-lg font-semibold text-gray-900 dark:text-white">{isTextOnly ? 'Create Facebook post' : 'Publish to Meta'}</h2><p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Facebook and Instagram publish public video. WhatsApp sends a video message to a recipient.</p></div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 px-6 py-5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Destination
            <select value={accountId} onChange={(event) => setAccountId(event.target.value)} disabled={isLoading || publishing} className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white">
              <option value="">{isLoading ? 'Loading channels…' : 'Select a connected channel'}</option>
              {accounts.filter((account) => !isTextOnly || account.platform === 'facebook').map((account: SocialAccount) => <option key={account._id} value={account._id}>{platformLabel[account.platform]} · {account.accountName}</option>)}
            </select>
          </label>
          {accounts.length === 0 && !isLoading && <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">Connect a channel in Integrations before publishing.</p>}
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Caption
            <textarea value={caption} onChange={(event) => setCaption(event.target.value)} maxLength={2200} rows={4} className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
          </label>
          {!isWhatsApp && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Hashtags
            <input value={hashtags} onChange={(event) => setHashtags(event.target.value)} placeholder="#reels #video" className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
          </label>}
          {isWhatsApp && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Recipient number
            <input value={recipient} onChange={(event) => setRecipient(event.target.value)} placeholder="15551234567" className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
          </label>}
          {!isWhatsApp && <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Schedule (optional)
            <input type="datetime-local" value={scheduledFor} onChange={(event) => setScheduledFor(event.target.value)} className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white" />
          </label>}
          {!isWhatsApp && !isTextOnly && (
            <div className="space-y-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={useOrgBranding} onChange={(e) => setUseOrgBranding(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                Use my organization&apos;s default intro/outro (if configured)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={applyFadeOut} onChange={(e) => setApplyFadeOut(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                Fade out at the end
              </label>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700"><button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700">Cancel</button><button type="submit" disabled={publishing || !accountId} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">{publishing ? 'Queueing…' : isWhatsApp ? 'Send WhatsApp message' : isTextOnly ? 'Publish post' : 'Publish video'}</button></div>
      </form>
    </div>
  );
}
